/**
 * AI Memory Entries panel — shows discrete ai_memory_entries rows (from the
 * send-triggered learning loop, Composer Phase 5) on both the account and
 * property KB pages.
 *
 * Suggested entries show Approve / Edit / Dismiss.
 * Approved entries show Edit / Remove (undo).
 * Entries flagged is_contradiction show a warning badge.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BrainCircuit,
  Check,
  X,
  Edit2,
  Save,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface AiMemoryEntry {
  id: number;
  userId: number;
  scope: string;
  propertyId: number | null;
  title: string;
  content: string;
  status: string;
  rationale: string | null;
  isContradiction: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AiMemoryEntriesPanelProps {
  listQueryKey: ReadonlyArray<unknown>;
  listEndpoint: string;
  canEdit: boolean;
  testIdPrefix: string;
  /**
   * Fail-closed AI Drafting access gate (must be computed as
   * `data?.allowed === true`, never `!== false`). When false the panel
   * renders nothing and never fires the ai-memory-entries fetch (which
   * would 403 for locked hosts).
   */
  aiDraftingAllowed: boolean;
}

function EntryRow({
  entry,
  canEdit,
  onDecide,
  testIdPrefix,
}: {
  entry: AiMemoryEntry;
  canEdit: boolean;
  onDecide: () => void;
  testIdPrefix: string;
}) {
  const { toast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(entry.content);
  const [showRationale, setShowRationale] = useState(false);

  const approveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/ai-memory-entries/${entry.id}/approve`, {});
      if (!res.ok) throw new Error("Failed to approve");
    },
    onSuccess: () => {
      toast({ title: "Memory approved and added to AI knowledge" });
      onDecide();
    },
    onError: () => toast({ title: "Failed to approve entry", variant: "destructive" }),
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/ai-memory-entries/${entry.id}/dismiss`, {});
      if (!res.ok) throw new Error("Failed to dismiss");
    },
    meta: { silentSave: true },
    onSuccess: () => {
      toast({ title: "Memory suggestion dismissed" });
      onDecide();
    },
    onError: () => toast({ title: "Failed to dismiss entry", variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await apiRequest("PUT", `/api/app/ai-memory-entries/${entry.id}`, { content });
      if (!res.ok) throw new Error("Failed to save");
      return (await res.json()) as AiMemoryEntry;
    },
    onSuccess: () => {
      toast({ title: entry.status === "approved" ? "Memory updated and re-indexed" : "Memory entry updated" });
      setEditMode(false);
      onDecide();
    },
    onError: () => toast({ title: "Failed to save entry", variant: "destructive" }),
  });

  const isSuggested = entry.status === "suggested";
  const isPending = approveMutation.isPending || dismissMutation.isPending || editMutation.isPending;

  return (
    <div
      className={`rounded-lg border p-3 text-sm ${
        entry.isContradiction
          ? "border-amber-200 bg-amber-50 dark:border-amber-700/40 dark:bg-amber-900/20"
          : isSuggested
          ? "border-blue-100 bg-blue-50/50 dark:border-blue-800/30 dark:bg-blue-900/10"
          : "border-green-100 bg-green-50/30 dark:border-green-800/30 dark:bg-green-900/10"
      }`}
      data-testid={`${testIdPrefix}-entry-${entry.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span
            className="font-medium text-gray-900 dark:text-gray-100 line-clamp-1"
            data-testid={`${testIdPrefix}-entry-title-${entry.id}`}
          >
            {entry.title}
          </span>
          {isSuggested && (
            <Badge
              variant="outline"
              className="text-[10px] h-4 py-0 border-blue-300 text-blue-700 dark:border-blue-600 dark:text-blue-300 shrink-0"
            >
              Suggested
            </Badge>
          )}
          {!isSuggested && (
            <Badge
              variant="outline"
              className="text-[10px] h-4 py-0 border-green-300 text-green-700 dark:border-green-600 dark:text-green-300 shrink-0"
            >
              Approved
            </Badge>
          )}
          {entry.isContradiction && (
            <Badge
              variant="outline"
              className="text-[10px] h-4 py-0 border-amber-400 text-amber-700 dark:border-amber-500 dark:text-amber-300 shrink-0 flex items-center gap-0.5"
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              May conflict
            </Badge>
          )}
        </div>
        {canEdit && !editMode && (
          <div className="flex items-center gap-1 shrink-0">
            {isSuggested && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-1.5 text-xs text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/40"
                onClick={() => approveMutation.mutate()}
                disabled={isPending}
                data-testid={`${testIdPrefix}-approve-${entry.id}`}
              >
                <Check className="h-3 w-3 mr-0.5" />
                Approve
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 px-1.5 text-xs text-muted-foreground hover:bg-muted"
              onClick={() => {
                setDraft(entry.content);
                setEditMode(true);
              }}
              disabled={isPending}
              data-testid={`${testIdPrefix}-edit-${entry.id}`}
            >
              <Edit2 className="h-3 w-3 mr-0.5" />
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
              onClick={() => dismissMutation.mutate()}
              disabled={isPending}
              data-testid={`${testIdPrefix}-dismiss-${entry.id}`}
            >
              <X className="h-3 w-3 mr-0.5" />
              {isSuggested ? "Dismiss" : "Remove"}
            </Button>
          </div>
        )}
      </div>

      {editMode ? (
        <div className="space-y-2 mt-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            className="text-xs font-mono"
            data-testid={`${testIdPrefix}-edit-textarea-${entry.id}`}
          />
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={() => setEditMode(false)}
              disabled={editMutation.isPending}
            >
              Cancel
            </Button>
            {isSuggested && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs text-green-700 hover:bg-green-100 dark:text-green-400"
                onClick={() => approveMutation.mutate()}
                disabled={editMutation.isPending || approveMutation.isPending}
              >
                <Check className="h-3 w-3 mr-0.5" />
                Approve as-is
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              className="h-6 px-2 text-xs bg-brand hover:bg-brand"
              onClick={() => editMutation.mutate(draft)}
              disabled={editMutation.isPending}
              data-testid={`${testIdPrefix}-save-edit-${entry.id}`}
            >
              <Save className="h-3 w-3 mr-0.5" />
              {editMutation.isPending ? "Saving…" : isSuggested ? "Save" : "Save & Re-index"}
            </Button>
          </div>
        </div>
      ) : (
        <p
          className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed"
          data-testid={`${testIdPrefix}-entry-content-${entry.id}`}
        >
          {entry.content}
        </p>
      )}

      {entry.rationale && !editMode && (
        <div className="mt-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-brand"
            onClick={() => setShowRationale((v) => !v)}
            data-testid={`${testIdPrefix}-rationale-toggle-${entry.id}`}
          >
            {showRationale ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
            Why this?
          </button>
          {showRationale && (
            <p className="text-[10px] text-muted-foreground mt-0.5 pl-1 border-l-2 border-muted">
              {entry.rationale}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function AiMemoryEntriesPanel({
  listQueryKey,
  listEndpoint,
  canEdit,
  testIdPrefix,
  aiDraftingAllowed,
}: AiMemoryEntriesPanelProps) {
  const entriesQuery = useQuery<AiMemoryEntry[]>({
    queryKey: listQueryKey,
    queryFn: async () => {
      const res = await fetch(listEndpoint, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load AI memory entries");
      return res.json();
    },
    enabled: aiDraftingAllowed === true,
  });

  if (aiDraftingAllowed !== true) {
    return null;
  }

  function refresh() {
    queryClient.invalidateQueries({ queryKey: listQueryKey });
  }

  const entries = entriesQuery.data ?? [];
  const suggested = entries.filter((e) => e.status === "suggested");
  const approved = entries.filter((e) => e.status === "approved");

  if (entriesQuery.isLoading) {
    return (
      <div className="mt-4 space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (entries.length === 0) return null;

  return (
    <div className="mt-4 space-y-3" data-testid={`${testIdPrefix}-entries-panel`}>
      {suggested.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <BrainCircuit className="h-3.5 w-3.5 text-brand" />
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Suggested by AI ({suggested.length})
            </h4>
            <span className="text-[10px] text-muted-foreground">
              — Approve to add to your AI knowledge base
            </span>
          </div>
          <div className="space-y-2">
            {suggested.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                canEdit={canEdit}
                onDecide={refresh}
                testIdPrefix={testIdPrefix}
              />
            ))}
          </div>
        </div>
      )}

      {approved.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <BrainCircuit className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Approved AI Memory ({approved.length})
            </h4>
          </div>
          <div className="space-y-2">
            {approved.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                canEdit={canEdit}
                onDecide={refresh}
                testIdPrefix={testIdPrefix}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
